import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showError, showSuccess } from "../../helpers/toast";
import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

API.interceptors.response.use(
  (response) => {
    showSuccess(response.data.message);
    return Promise.resolve(response);
  },
  (error) => {
    showError(
      error.response?.data?.error ??
      error.response?.data?.message ??
      error.message
    );
    return Promise.reject(error);
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${userData.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(`/users/${userId}`, userData, config);
      showSuccess("Update Successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const getUser = createAsyncThunk(
  "users/getUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${userData.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get(`/users/${userId}`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

// Async thunk for deleting a company
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ configdata }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.delete(`/users/${configdata.user_id}`, config);
      showSuccess("Delete Successfully");
      return configdata.company_id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const getallUser = createAsyncThunk(
  "users/getallUser",
  async (data, { rejectWithValue }) => {
    try {
      // Support both old format (just authToken string) and new format (object with authToken and queryParams)
      const authToken = typeof data === 'string' ? data : data?.authToken;
      const queryParams = typeof data === 'object' && data?.queryParams ? data.queryParams : {};

      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${authToken}`,
          "Content-Type": "application/json",
        },
      };

      // Build URL with query parameters if provided
      let url = `/users/`;
      if (queryParams && Object.keys(queryParams).length > 0) {
        const queryString = new URLSearchParams(queryParams).toString();
        url = `${url}?${queryString}`;
      }

      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const updatePassword = createAsyncThunk(
  "users/updatePassword",
  async ({ userId, userData, token, onSuccess }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(
        `/users/change-password/${userId}`,
        userData,
        config
      );
      onSuccess();
      // showSuccess("Update Successfully!");
      return response.data;
    } catch (error) {
      console.log(error, "rejected");
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

const initialState = {
  getuserdata: {},
  getalluserdata: [],
  loading: false,
  error: null,
  message: null,
  updateLoader: false,
  passwordLoader: false,
  passwordError: null,
};

const userSlice = createSlice({
  name: "userdata",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(updateUser.pending, (state) => {
        state.updateLoader = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateLoader = false;
        state.message = action.payload?.message;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateLoader = false;
        state.error = action.payload;
      })
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.getuserdata = action.payload;
        state.error = null;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getallUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getallUser.fulfilled, (state, action) => {
        state.loading = false;
        // Store raw payload - filtering will be done at component level based on user role
        // This allows components to access all users if needed (e.g., for merging application data)
        // but API-level filtering should prevent unauthorized access
        state.getalluserdata = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getallUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.getalluserdata = state.getalluserdata.filter(
          (user) => user.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePassword.pending, (state) => {
        state.passwordLoader = true;
        state.passwordError = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.passwordLoader = false;
        state.message = action.payload?.message;
        state.passwordError = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.passwordLoader = false;
        state.passwordError = action.payload;
      });
  },
});

export default userSlice.reducer;
